import type { SearchClass } from '@Types/Search/Search';

import { useEffect, useRef, useState } from 'react';

import type { ReactElement } from '@wordpress/element/build-types/serialize';

import { cn } from '@Blocks/_components/ui/utils';

import type { ActionMenuElementDecorations } from '@/src/Types/Shared/Header';
import { __Front } from '@/src/Views/Shared/lib';
import { useClickAway } from '@uidotdev/usehooks';

import {
	CloseLightIcon,
	CloseRoundIcon,
	CloseSmallIcon,
	SearchBigIcon,
	SearchLightIcon,
	SearchSmallIcon,
} from '../../Icons';

export default function Search({
	homeUrl,
	itemClass,
	actionIconsStyle,
}: {
	homeUrl: string;
	itemClass: SearchClass;
	actionIconsStyle?: ActionMenuElementDecorations;
}) {
	const [openSearch, setOpenSearch] = useState<boolean>(false);
	const myRef = useRef<HTMLInputElement>(null);

	const ToogleSearch = () => {
		if (openSearch) {
			setOpenSearch(false);
		} else {
			setOpenSearch(true);
			const close = (e: any) => {
				if (e.keyCode === 27) {
					setOpenSearch(false);
				}
			};
			window.addEventListener('keydown', close);
		}
	};

	const modal = useClickAway<HTMLInputElement>(() => {
		setOpenSearch(false);
	});

	useEffect(() => {
		if (openSearch) {
			document.documentElement.style.overflow = 'hidden';
			document.documentElement.style.height = 'h-screen';
			window.setTimeout(function () {
				myRef.current?.focus();
			}, 100);
		} else {
			document.documentElement.style.overflow = 'visible';
			document.documentElement.style.height = 'auto';
		}
	}, [openSearch]);

	let classForm = 'invisible pointer-events-none translate-y-0 opacity-0';
	const invisible = 'opacity-0 w-0 h-0 invisible';
	const visible = 'opacity-40 w-screen h-screen min-h-full visible tablet:translate-0';

	if (openSearch) {
		classForm = 'visible pointer-events-auto translate-y-10 desktop:translate-y-16 opacity-1';
	}

	const displaySearchIcon = (actionIconsStyle: ActionMenuElementDecorations) => {
		let element: ReactElement;

		const style = cn('transition-all inline-bloc ', itemClass?.iconColor);

		if (actionIconsStyle) {
			switch (actionIconsStyle) {
				case 'round':
					element = <SearchBigIcon className={style} />;
					break;
				case 'small':
					element = <SearchSmallIcon className={style} />;
					break;
				case 'light':
					element = <SearchLightIcon className={style} />;
					break;
				default:
					element = <SearchBigIcon className={style} />;
			}
			return element;
		}

		return null;
	};

	const displayCloseIcon = (actionIconsStyle: ActionMenuElementDecorations) => {
		let element: ReactElement;

		const style = cn('inline-bloc ', itemClass?.iconColor);

		if (actionIconsStyle) {
			switch (actionIconsStyle) {
				case 'round':
					element = <CloseRoundIcon className={style} />;
					break;
				case 'small':
					element = <CloseSmallIcon className={style} />;
					break;
				case 'light':
					element = <CloseLightIcon className={style} />;
					break;
				default:
					element = <CloseRoundIcon className={style} />;
			}
			return element;
		}

		return null;
	};

	return (
		<div className={cn('group flex', itemClass.wrapper)}>
			<div
				className={cn('z-30 block fixed left-0 top-0 bg-black', openSearch ? visible : invisible)}
			></div>
			<button className='group' onClick={ToogleSearch}>
				<span className='sr-only'>
					{openSearch ? __Front('front.search.close') : __Front('front.search.open')}
				</span>

				{actionIconsStyle && displaySearchIcon(actionIconsStyle)}
			</button>
			{/* STYLE INFO ON MOBILE : SEARCH BAR */}
			{/* by default, the search bar takes options set in main navbar */}
			{/* therefore, it is mandatory to set set values also in main (primary) navbar even if the search bar will only be in top (secondary) navbar in desktop version */}
			<div
				ref={modal}
				className={cn(
					'transition-all absolute flex rounded-minos-header-mainnav-search-radius minos-header-mainnav-search-border left-1/2 -translate-x-1/2 top-full z-[999] shadow-lg w-full desktop:w-1/2',
					classForm,
					itemClass.searchRadius,
					itemClass?.searchBg,
					itemClass?.border
				)}
			>
				<form
					role='search'
					action={homeUrl}
					method='get'
					className={cn('w-full flex rounded-minos-header-mainnav-search-radius', itemClass?.search)}
				>
					<button className='group px-8'>
						<span className='sr-only'>{__Front('front.search.search')}</span>
						{actionIconsStyle && displaySearchIcon(actionIconsStyle)}
					</button>
					<label className='invisible absolute size-0 opacity-0' htmlFor='search'>
						{__Front('front.search.search')}
					</label>
					<input
						ref={myRef}
						id='search'
						type='text'
						name='s'
						placeholder={__Front('front.search.search')}
						className={cn('w-full py-8 focus-within:outline-none', itemClass?.searchBg)}
					/>
				</form>
				<button className='group px-8' onClick={ToogleSearch}>
					<span className='sr-only'>{__Front('front.search.close')}</span>
					{actionIconsStyle && displayCloseIcon(actionIconsStyle)}
				</button>
			</div>
		</div>
	);
}
